import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';
import { Crop } from 'react-image-crop';

import { Alert, AlertSize, AlertType } from '../../components/Alert/Alert';
import { ButtonDecorator } from '../../components/ButtonDecorator/ButtonDecorator';
import { ModalDecorator } from '../../components/ModalDecorator/ModalDecorator';
import { Bem } from '../../service/Bem/Bem';
import { Size, ValidationErrorData } from '../../typings/common';
import { CreativeImageCrop } from '../CreativeImageCrop/CreativeImageCrop';

import './CreativeSmartCenterModal.css';
import * as keyset from './CreativeSmartCenterModal.i18n';

const i18n = i18nFactory(keyset);

export interface CreativeSmartCenterModalProps {
    isVisible: boolean;
    isLoading?: boolean;
    error?: ValidationErrorData;
    onClose: () => void;
    onSave: (image: HTMLImageElement) => void;

    src?: string;
    crop?: Crop;
    onCropComplete: (crop: Crop) => void;
    onImageLoaded: (naturalImageSize: Size, imageSize: Size) => void;
}

const BLOCK = 'creative-smart-center-modal';

export class CreativeSmartCenterModal extends React.PureComponent<CreativeSmartCenterModalProps> {
    static BottomControls = React.memo(
        ({ onSave, onClose, saveDisabled }: { onClose: () => void; onSave: () => void; saveDisabled?: boolean }) => (
            <>
                <ButtonDecorator
                    cls={Bem.cls(BLOCK, 'add-button')}
                    size="n"
                    text={i18n('set-smart-center')}
                    theme="action"
                    view="classic"
                    onClick={onSave}
                    disabled={saveDisabled}
                />
                <ButtonDecorator
                    cls={Bem.cls(BLOCK, 'cancel-button')}
                    size="n"
                    text={i18n('cancel')}
                    theme="normal"
                    view="classic"
                    onClick={onClose}
                />
            </>
        )
    );

    private image: HTMLImageElement;

    render(): React.ReactNode {
        const { isVisible, isLoading, src, crop, onCropComplete } = this.props;

        return (
            <ModalDecorator
                header={i18n('smart-center-title')}
                footer={this.renderFooter()}
                visible={isVisible}
                progress={isLoading}
                cls={BLOCK}
                theme="full-width-body-with-borders"
            >
                {src && (
                    <CreativeImageCrop
                        cls={Bem.cls(BLOCK, 'content-wrapper', {
                            loading: !crop
                        })}
                        src={src}
                        crop={crop}
                        onCropComplete={onCropComplete}
                        onImageLoaded={this.onImageLoaded}
                    />
                )}
            </ModalDecorator>
        );
    }

    private renderFooter = (): React.ReactNode => {
        const { onClose, error, src } = this.props;

        return (
            <>
                {this.renderError()}
                <CreativeSmartCenterModal.BottomControls
                    onSave={this.onSave}
                    onClose={onClose}
                    saveDisabled={!src || Boolean(error)}
                />
            </>
        );
    };

    private renderError = (): React.ReactNode => {
        const { error } = this.props;

        if (!error) {
            return null;
        }

        return (
            <div className={Bem.cls(BLOCK, 'error')}>
                <Alert type={AlertType.error} size={AlertSize.s}>
                    {error.text}
                </Alert>
            </div>
        );
    };

    private onSave = (): void => {
        if (this.image !== undefined) {
            this.props.onSave(this.image);
        }
    };

    private onImageLoaded = (naturalImageSize: Size, imageSize: Size, image: HTMLImageElement): void => {
        this.image = image;

        this.props.onImageLoaded(naturalImageSize, imageSize);
    };
}
